"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/authContext";
import { Eye, EyeClosed } from "lucide-react";

interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

export default function LoginForm({
  onSuccess,
  redirectTo = "/",
}: LoginFormProps) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { login, isAuthenticated } = useAuth();

  const handleSignIn = async () => {
    setError("");
    setIsLoading(true);

    if (!identifier || !password) {
      setError("All fields are required");
      setIsLoading(false);
      return;
    }

    try {
      await login(identifier, password);
      if (onSuccess) {
        onSuccess();
      } else {
        router.replace(redirectTo);
      }
    } catch (e) {
      const errorMessage = "Authentication failed";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "";
    const REDIRECT_URI =
      process.env.NEXT_PUBLIC_FRONTEND_URL + "/google-callback";
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_type: "token id_token",
      scope: "openid email profile",
      include_granted_scopes: "true",
      state: "login",
      nonce: "random_nonce", // optional but recommended
      prompt: "select_account", // forces account selection
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const handleForgotPassword = () => {
    router.push("/forgot-password");
  };
  if (isAuthenticated) {
    return null;
  }

  return (
    <motion.div
      className="pointer-events-auto flex  max-w-md "
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ type: "tween", ease: "anticipate", duration: 0.5 }}
    >
      <motion.div
        className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 dark:border-gray-700/30"
        whileHover={{ scale: 1.01 }}
        transition={{ duration: 0.2 }}
      >
        <h1 className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Log in to SocialGraphs
        </h1>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100/80 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 p-4 rounded-xl mb-6 backdrop-blur-sm"
          >
            <p className="font-medium">{error}</p>
            {error.includes("not registered") && (
              <p className="mt-2 text-sm">
                New to SocialGraphs?{" "}
                <Link
                  href="/signup"
                  className="text-blue-500 hover:underline font-medium"
                >
                  Create an account
                </Link>
              </p>
            )}
          </motion.div>
        )}

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-2 bg-white/60 dark:bg-gray-800/60 border border-gray-300/50 dark:border-gray-600/50 rounded-xl py-3 px-4 mb-3 font-medium hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200 backdrop-blur-sm shadow-lg"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </motion.button>

        <div className="flex items-center justify-center mb-3">
          <div className="border-t border-gray-300/50 dark:border-gray-600/50 w-full"></div>
          <div className="text-gray-500 dark:text-gray-400 px-4">or</div>
          <div className="border-t border-gray-300/50 dark:border-gray-600/50 w-full"></div>
        </div>

        <div className="space-y-4">
          <motion.div
            whileFocus={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <input
              data-cy="email-input"
              type="text"
              placeholder="Email or Username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full bg-white/60 dark:bg-gray-800/60 border border-gray-300/50 dark:border-gray-600/50 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent backdrop-blur-sm shadow-lg transition-all duration-200"
            />
          </motion.div>
          <motion.div
            className="relative w-full max-w-sm"
            whileFocus={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <input
              data-cy="password-input"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/60 dark:bg-gray-800/60 border border-gray-300/50 dark:border-gray-600/50 rounded-xl p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent backdrop-blur-sm shadow-lg transition-all duration-200"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 px-3 text-sm text-blue-500 hover:text-blue-600 transition-colors"
            >
              {showPassword ? <Eye /> : <EyeClosed />}
            </button>
          </motion.div>
          <div className="text-right">
            <button
              onClick={handleForgotPassword}
              className="text-blue-500 dark:text-blue-400 hover:underline text-sm transition-colors"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <motion.button
          data-cy="login-button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          onClick={handleSignIn}
          disabled={isLoading}
          className="w-full mt-4 btn-primary-theme"
        >
          {isLoading ? "Signing in..." : "Sign in"}
        </motion.button>

        <div className="mt-8 text-center">
          <p className="text-gray-600 dark:text-gray-400 ">
            Don&apos;t have an account?
          </p>
          <button
            onClick={() => router.push("/signup")}
            className="w-[70%] mt-4 bg-black dark:bg-white p-2 rounded-full dark:text-black text-white text-base font-semibold"
          >
            Sign up
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
